<template>
  <el-form class="backtest-form">
    <!-- Timezone -->
    <el-form-item label="Timezone">
      <el-select
        class="utc-select"
        v-model="form.offset"
        size="small"
        :placeholder="$t('text.please_select')"
        filterable
      >
        <el-option
          v-for="item in utcOffsetOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        >
        </el-option>
      </el-select>
    </el-form-item>

    <!-- Selection Mode Toggle -->
    <el-form-item label="Selection Mode">
      <el-radio-group
        v-model="form.selectionMode"
        @change="handleSelectionModeChange"
      >
        <el-radio label="device">Device(s)</el-radio>
        <el-radio label="patient">Patient(s)</el-radio>
      </el-radio-group>
    </el-form-item>

    <!-- Device ID(s) - Multi Select -->
    <el-form-item v-if="form.selectionMode === 'device'" label="Device ID">
      <el-select
        v-model="form.deviceIds"
        multiple
        placeholder="Select devices"
        filterable
        @change="onDeviceChange"
      >
        <el-option
          v-for="pod in deviceOptions"
          :key="pod.podId"
          :label="pod.podId"
          :value="pod.podId"
        >
        </el-option>
      </el-select>
    </el-form-item>

    <!-- Patient ID(s) - Multi Select -->
    <el-form-item v-if="form.selectionMode === 'patient'" label="Patient ID">
      <el-select
        v-model="form.patientIds"
        multiple
        placeholder="Select patients"
        filterable
        @change="onPatientChange"
      >
        <el-option
          v-for="patient in patientOptions"
          :key="patient.PatientId"
          :label="patient.Alias"
          :value="patient.PatientId"
        >
        </el-option>
      </el-select>
    </el-form-item>

    <!-- Time Period -->
    <el-form-item label="Time Period" class="date-field">
      <el-date-picker
        v-model="form.dateRange"
        type="datetimerange"
        range-separator="-"
        start-placeholder="Start date"
        end-placeholder="End date"
        value-format="yyyy-MM-dd HH:mm:ss"
      >
      </el-date-picker>
    </el-form-item>

    <!-- Algorithm Type -->
    <el-form-item label="Algorithm Type">
      <el-select v-model="form.algorithmType" placeholder="Select algorithm">
        <el-option
          v-for="option in algorithmTypeOptions"
          :key="option.value"
          :label="option.label"
          :value="option.value"
        />
      </el-select>
    </el-form-item>

    <!-- Start Test Button -->
    <el-button
      @click="createTest"
      :disabled="!isFormValid || creating"
      :loading="creating"
      type="primary"
      class="start-test-btn"
    >
      {{ creating ? 'CREATING...' : 'START TEST' }}
    </el-button>
  </el-form>
</template>

<script>
import moment from 'moment-timezone'
import { utcOffsets } from '@/const'
import BackTestApiService from '@/services/BackTestApiService'
import swal from 'sweetalert'
import { jobTypes } from '@/const'

export default {
  name: 'BackTestForm',
  data() {
    return {
      form: {
        offset: this.$store.state.pods.selectedUtcOffset,
        selectionMode: 'device',
        deviceIds: [],
        patientIds: [],
        dateRange: [],
        algorithmType: 'newpad-v1.0'
      },
      creating: false,
      utcOffsetOptions: utcOffsets,
      algorithmTypeOptions: [],
      deviceOptions: this.$store.state.pods.pods,
      patientOptions: []
    }
  },
  mounted() {
    this.fetchAlgorithmOptions()
    this.fetchDevices()
    this.fetchPatients()
  },
  computed: {
    isFormValid() {
      const hasSelection =
        this.form.selectionMode === 'device'
          ? this.form.deviceIds.length > 0
          : this.form.patientIds.length > 0

      return (
        hasSelection &&
        Array.isArray(this.form.dateRange) &&
        this.form.dateRange.length === 2 &&
        this.form.dateRange[0] &&
        this.form.dateRange[1] &&
        this.form.algorithmType
      )
    }
  },
  methods: {
    async fetchDevices() {
      await this.$store.dispatch('pods/fetchPods')
      this.deviceOptions = this.$store.state.pods.pods
    },
    async fetchPatients() {
      const response = await BackTestApiService.GetAllPatientsForConfig()
      this.patientOptions = response.data.data
    },
    async fetchAlgorithmOptions() {
      const response = await BackTestApiService.getAlgorithmOptions()
      this.algorithmTypeOptions = response.data.map(algo => ({
        label: algo,
        value: algo
      }))
    },
    handleSelectionModeChange(mode) {
      // Clear the other selection when mode changes
      if (mode === 'device') {
        this.form.patientIds = []
      } else {
        this.form.deviceIds = []
      }
    },
    onDeviceChange() {
      // Auto-clear patients if in device mode
      this.form.patientIds = []
    },
    onPatientChange() {
      // Auto-clear devices if in patient mode
      this.form.deviceIds = []
    },
    async createTest() {
      if (!this.isFormValid) return

      this.creating = true
      try {
        const isNewPad = this.form.algorithmType.indexOf('newpad') !== -1
        const isVolumeEstimation =
          this.form.algorithmType.indexOf('volumeestimation') !== -1

        let jobType = jobTypes.backTestingRedAlert // default to red alert
        if (isNewPad) {
          jobType = jobTypes.backTestingNewPad
        } else if (isVolumeEstimation) {
          jobType = jobTypes.backTestingVolumeEstimation
        }

        const parameters = {
          startTime: this.form.dateRange[0],
          endTime: this.form.dateRange[1],
          offset: moment.tz(this.form.offset).utcOffset() / 60,
          algorithmType: this.form.algorithmType,
          deviceIds:
            this.form.selectionMode === 'device' ? this.form.deviceIds : [],
          patientIds:
            this.form.selectionMode === 'patient' ? this.form.patientIds : []
        }

        const response = await BackTestApiService.createBackTest({
          jobType: jobType,
          parameters: parameters
        })

        swal({
          icon: 'success',
          title: 'Success',
          text: 'Test created successfully'
        })

        this.$emit('test-created', response.data.jobId)
      } catch (error) {
        swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to create test'
        })
      } finally {
        this.creating = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.backtest-form {
  ::v-deep .el-form-item {
    display: block;
    margin-bottom: 13px;
  }

  ::v-deep .el-form-item__label {
    display: block;
    float: none;
    width: auto;
    margin-bottom: 6px;
    padding: 0;
    color: var(--sidebar-t2);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    line-height: 1.3;
    text-align: left;
    text-transform: uppercase;
  }

  ::v-deep .el-form-item__content {
    margin-left: 0 !important;
    line-height: 32px;
  }

  ::v-deep .el-select,
  ::v-deep .el-input,
  ::v-deep .el-date-editor {
    width: 100%;
  }

  ::v-deep .el-input__inner,
  ::v-deep .el-range-editor.el-input__inner {
    height: 36px;
    border-color: #3a455c;
    border-radius: 6px;
    background: var(--sidebar2);
    color: var(--sidebar-t);
    font-size: 12px;

    &::placeholder {
      color: #7f8ba1;
    }

    &:hover,
    &:focus {
      border-color: var(--sidebar-a);
    }
  }

  ::v-deep .el-range-editor {
    padding: 3px 9px;

    .el-range-input {
      background: transparent;
      color: var(--sidebar-t);
      font-size: 11px;

      &::placeholder {
        color: #7f8ba1;
      }
    }

    .el-range-separator,
    .el-range__icon,
    .el-range__close-icon {
      color: var(--sidebar-t2);
      line-height: 28px;
    }
  }

  ::v-deep .el-input__prefix,
  ::v-deep .el-input__suffix {
    color: var(--sidebar-t2);
  }

  ::v-deep .el-radio {
    margin-right: 20px;
    color: var(--sidebar-t2);
    font-size: 12px;

    .el-radio__label {
      padding-left: 7px;
      font-size: 12px;
    }

    &.is-checked .el-radio__label {
      color: #fff;
    }
  }

  ::v-deep .el-tag {
    border-color: #4c5a74;
    background: #354159;
    color: var(--sidebar-t);
  }

  .start-test-btn {
    width: 100%;
    height: 38px;
    margin-top: 3px;
    border-radius: 6px;
    font-weight: 600;
    font-size: 12px;
    letter-spacing: 0.08em;

    &.is-disabled {
      border-color: #344056;
      background: #344056;
      color: #77849a;
    }
  }
}
</style>
